import React, { ReactElement, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Loader } from 'idsk-react-components';
import {
  Breadcrumbs, getTitle, homeBreadcrumb, setTitle,
} from 'slovlex-idsk-react-components';
import routes from '../../routing/routes';
import {
  useLegislativnyMaterialDetail,
} from '../../module/LegislativnyMaterial/LegislativnyMaterialDetail';
import PageHeadingWithBackButton from '../../module/PageHeadingWithBackButton';
import SprievodnyDokumentUpdateList
  from '../../module/SprievodnyDokument/SprievodnyDokumentUpdateList';
import GlobalErrors from '../../module/GlobalErrors';
import Layout from '../Layout';
import SecuredPage from '../../module/SecuredPage';
import PracovnyKrokEnum from '../../model/pracovnyKrok/PracovnyKrokEnum';
import { pouzivatelIsAdmin } from '../../function/permissions';
import { useUserContext } from '../../context/UserContext';
import SuccessMessages from '../../module/SuccessMessages';
import useLegislativnyMaterialHistoria
  from '../../module/LegislativnyMaterial/LegislativnyMaterialHistoria/hooks/useLegislativnyMaterialHistoria';
import { currentStadiumEqualsLatest } from '../../function/getLatestStadium';

const SprievodnyDokumentUpdateListPage = (): ReactElement => {
  const location = useLocation();
  const { pouzivatel } = useUserContext();
  const navigate = useNavigate();
  const {
    materialDetail,
    materialStatus,
  } = useLegislativnyMaterialDetail(PracovnyKrokEnum.SPRIEVODNY_DOKUMENT_FUNCTION);
  const { search } = useLocation();
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();

  const {
    legMaterialHistoriaList,
    legMaterialHistoriaStatus,
  } = useLegislativnyMaterialHistoria(materialDetail?.uuid);

  function isPredbeznaInformacia(): boolean {
    return type === 'PI';
  }

  const canEditDocuments = pouzivatelIsAdmin(pouzivatel)
      || (materialDetail?.hasOpravnenieSprievodneDokumenty && materialDetail?.hasOpravneniePracovneKroky
          && currentStadiumEqualsLatest(materialDetail?.stadium?.hodnota, legMaterialHistoriaList?.historiaRecords));

  useEffect(() => {
    if (legMaterialHistoriaList && materialDetail && !canEditDocuments) {
      const stadiumUuidSearchParam = new URLSearchParams(search).get('stadiumUuid');
      navigate(routes.procesy.detail.sprievodneDokumenty.list.withParams(type, year, index, stadiumUuidSearchParam));
    }
  }, [canEditDocuments, index, legMaterialHistoriaList, materialDetail, navigate, search, type, year]);

  useEffect(() => {
    if (!legMaterialHistoriaStatus.loading && !materialStatus.loading) {
      setTitle(getTitle());
    }
  }, [legMaterialHistoriaStatus.loading, materialStatus.loading]);

  return (
    <Layout>
      <>
        <Breadcrumbs routeList={[
          homeBreadcrumb(process.env.REACT_APP_PORTAL_MAIN_URL),
          {
            heading: isPredbeznaInformacia() ? 'Predbežné informácie' : 'Legislatívne procesy',
            to: isPredbeznaInformacia() ? routes.procesy.predbeznaInformacia.list : routes.procesy.list,
          },
          {
            heading: `${type}/${year}/${index}`,
            to: routes.procesy.detail.withParams(type, year, index),
          },
          {
            heading: 'Sprievodná dokumentácia',
            to: location.pathname,
          }]}
        />
        <GlobalErrors />
        <Loader loading={materialStatus.loading || legMaterialHistoriaStatus.loading}>
          <SecuredPage
            additionalCheck={canEditDocuments}
            requestStatus={materialStatus.responseCode}
          >
            <>
              <SuccessMessages />
              {materialDetail && legMaterialHistoriaList && (
                <>
                  <PageHeadingWithBackButton
                    headingCaption={`Legislatívny proces ${materialDetail.cisloLegislativnehoMaterialu}`}
                    heading="Sprievodná dokumentácia"
                    backLinkText={`Späť na ${materialDetail.cisloLegislativnehoMaterialu}`}
                    backLinkRoute={routes.procesy.detail.withParams(type, year, index)}
                  />
                  <SprievodnyDokumentUpdateList
                    materialDetail={materialDetail}
                    legMaterialHistoriaList={legMaterialHistoriaList}
                  />
                </>
              )}
            </>
          </SecuredPage>
        </Loader>
      </>
    </Layout>
  );
};
export default SprievodnyDokumentUpdateListPage;
